import React from 'react';
import {StorageKey} from "../../../enums/StorageKey";
import {Trans} from "@lingui/macro";
import BukkuTooltip from "../../Tooltip";
import {EyeInvisibleOutlined} from "@ant-design/icons";
import AmountTd from "../../Table/Common/AmountTd";
import {CreatedAtColumn} from "../../Table/Common/CreatedAtTd";
import Quantity from "../../Quantity";
import ProductBundleListMenu from "../../ListMenu/ProductBundle";
import ProductListMenu from "../../ListMenu/Product";
import {ImageThumbnail} from "../../ImageThumbnail";
import {BundleImageTag} from "../../ImageThumbnail/BundleImageTag";
import Tags from "../../Table/Cell/Tags";

const MAX_TEXT_LENGTH_FOR_NORMAL_ELLIPSIS = 20;

function getTextWidth(text, font) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    context.font = font || getComputedStyle(document.body).font;

    return context.measureText(text).width;
}

function renderName(text, storageData, key, minWidth) {
    const currentWidth = storageData?.columns.find(col => col.key === key)?.width || minWidth;
    const nameHeaderDom = document.getElementsByClassName('ant-table-cell product-name-column react-resizable');
    const textContainerDom = document.getElementsByClassName('product-text-span');
    const currentWidthScaled = nameHeaderDom.length > 0 ? nameHeaderDom[0].offsetWidth : currentWidth;

    if (text.length > MAX_TEXT_LENGTH_FOR_NORMAL_ELLIPSIS) {
        // Original ellipsis: min width of 150px, max can display 20 chars, so is 20 chars + '...';
        // Here with manual middle ellipsis, the 20 chars should include the 3 dots,
        // So it'll be like 10 chars + '...' + 7 chars = 20 chars (where 3 chars are from the '...')

        // const firstHalfLength = 10 + Math.floor((currentWidth - minWidth) / 10); // every 10 px, display one more char
        const firstHalfLengthScaled = 10 + Math.floor((currentWidthScaled - minWidth) / 10); // every 10 px, display one more char
        const secondHalfLength = Math.max(10, Math.floor(firstHalfLengthScaled / 2)); // 10 chars min, every 2 leading chars, display one more trailing char

        const nameWidth = getTextWidth(text, '14px Roboto, sans-serif');
        // console.log('aaa textContainerDom', {text, textContainerDom, nameWidth, currentWidthScaled, textContainerDomWidth: textContainerDom[0]?.offsetWidth});
        if (textContainerDom && nameWidth <= textContainerDom[0]?.offsetWidth)
            return text;

        // if all text already expanded, no need for ellipsis anymore, just display whole text
        if (firstHalfLengthScaled + secondHalfLength >= text.length - 1)
            return text;

        // const firstHalfText = text.substring(0, firstHalfLengthScaled);
        // const secondHalfText = text.substring(text.length - secondHalfLength, text.length);

        return (
        // <span className="product-text-span" style={{whiteSpace: 'pre'}}>
            //     {
            //         `${firstHalfText}...${secondHalfText}`
            //     }
            // </span>
            <div
                className="product-text-span"
                style={{
                    display: 'flex',
                    width: '100%',
                }}
            >
                <span
                    style={{
                        overflow: 'hidden',
                        whiteSpace: 'pre',
                        flex: `0 1 ${firstHalfLengthScaled}ch`,
                    }}
                >
                    {text}
                </span>
                ...
                <span
                    style={{
                        marginRight: 'auto',
                        textAlign: 'right',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        flex: `0 1 ${secondHalfLength}ch`,
                    }}
                >
                    <span style={{float: 'right'}}>
                        {text}
                    </span>
                </span>
            </div>

        );
    }

    return text;
}

function getColumns(props) {
    const { companyCurrencyCode, storageData } = props;

    const nameColWidth = 150;

    return [
        {
            key: 'thumbnail_url',
            title: 'Thumbnail',
            width: 75,
            render: (text, record) => {
                const src = record.thumbnail_url || '/assets/img/product-placeholder.png';
                return <ImageThumbnail src={src} extra={record.type === 'bundle' && <BundleImageTag />} />;
            },
        },
        {
            title: <Trans>Name</Trans>,
            dataIndex: 'name',
            key: 'name',
            width: nameColWidth,
            className: 'product-name-column',
            render: (text, record) => (
                <BukkuTooltip
                    placement="topLeft"
                    title={text}
                    className="center-vertically"
                >
                    <span className="product-text-container full-width">
                        {/* <div className={text.length > MAX_TEXT_LENGTH_FOR_NORMAL_ELLIPSIS ? '' : 'ellipsis-div'}> */}
                        {renderName(text, storageData, 'name', nameColWidth)}
                    </span>
                    &nbsp;
                    {record.is_archived ? <EyeInvisibleOutlined /> : null}
                </BukkuTooltip>
            ),
        },
        {
            title: <Trans>SKU</Trans>,
            dataIndex: 'sku',
            key: 'sku',
            width: 140,
            ellipsis: true,
            render: text => (
                <div className="ellipsis-div">
                    <BukkuTooltip>
                        <span>{text || '-'}</span>
                    </BukkuTooltip>
                </div>
            ),
        },
        {
            title: <Trans>Groups</Trans>,
            dataIndex: 'group_names',
            key: 'group_names',
            width: 140,
            ellipsis: true,
            render: text => <Tags list={text || []} />,
        },
        {
            title: <Trans>Bin Location</Trans>,
            dataIndex: 'bin_location',
            key: 'bin_location',
            width: 140,
            ellipsis: true,
            render: text => (
                <div className="ellipsis-div">
                    <BukkuTooltip>
                        <span>{text || '-'}</span>
                    </BukkuTooltip>
                </div>
            ),
        },
        {
            title: <Trans>Quantity</Trans>,
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            width: 130,
            render: (text, record) => {
                if (record?.type === 'bundle')
                    return '-';

                if (!record.track_inventory)
                    return '-';

                return <Quantity
                    amount={text}
                    lowTrigger={record.quantity_low_alert}
                    productId={record.id}
                />;
            },
        },
        {
            title: <Trans>Sale Price</Trans>,
            dataIndex: 'sale_price',
            key: 'sale_price',
            align: 'right',
            width: 150,
            render: (text, record) => {
                if (record?.type === 'bundle')
                    return '-';
                return <AmountTd currency_code={companyCurrencyCode} amount={text} />;
            },
        },
        {
            title: <Trans>Purchase Price</Trans>,
            dataIndex: 'purchase_price',
            key: 'purchase_price',
            align: 'right',
            width: 150,
            render: (text, record) => {
                if (record?.type === 'bundle')
                    return '-';
                return <AmountTd currency_code={companyCurrencyCode} amount={text} />;
            },
        },
        {
            title: <Trans>Margin %</Trans>,
            dataIndex: 'margin_percent',
            key: 'margin_percent',
            align: 'right',
            width: 100,
            render: (text, record) => {
                if (record?.type === 'bundle')
                    return '-';
                if (record) {
                    const marginPercent = (
                        ((record.sale_price - record.purchase_price) / record.sale_price) * 100);
                    if (
                        Number.isNaN(marginPercent) ||
                        marginPercent === null ||
                        marginPercent === -Infinity ||
                        marginPercent === Infinity ||
                        marginPercent === 100
                    )
                        return '-';

                    return `${marginPercent.toFixed(2)}%`;
                }
                return null;
            },
        },
        CreatedAtColumn,
        {
            title: <Trans>Action</Trans>,
            key: 'action',
            width: 120,
            render: (text, record) => {
                if (record?.type === 'bundle')
                    return <ProductBundleListMenu record={record} />;
                return <ProductListMenu record={record} />;
            },
        },
    ];
}

export default {
    storageKey: StorageKey.product_items,
    sortable: [
        'name',
        'sku',
        'quantity',
        'bin_location',
        'sale_price',
        'purchase_price',
        'created_at',
    ],
    required: [
        'name',
        'action',
    ],
    defaulto: [
        'thumbnail_url',
        'sku',
        'quantity',
        'sale_price',
        'purchase_price',
    ],
    hideTitle: [
        'thumbnail_url',
    ],
    getColumns,
};
